/**
 * admin/src/pages/BackupPage.tsx
 * Trang quản lý Backup D1 + R2 → Google Drive
 *
 * Sections:
 *  1. Status Header — trạng thái tổng quan
 *  2. Cấu hình Auto Backup — bật/tắt, auto-delete 30d
 *  3. Backup Thủ công — nút trigger + confirm dialog
 *  4. Lịch sử Backup — 20 bản gần nhất từ KV
 *  5. Hướng dẫn Khôi phục (Restore Guide)
 */

import { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../hooks/useAdmin';
import { toast } from '../components/ui/index';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BackupConfig {
  isEnabled: boolean;
  autoDelete30d: boolean;
  updatedAt: string;
}

interface BackupLogEntry {
  id: string;
  type: 'auto' | 'manual';
  status: 'success' | 'failed' | 'running';
  triggeredBy: string;
  startedAt: string;
  finishedAt: string | null;
  sizeSqlBytes: number;
  message: string;
}

interface BackupStatus {
  config: BackupConfig;
  isRunning: boolean;
  runningInfo: { runId: string; triggeredBy: string; startedAt: string } | null;
  lastBackupAt: string | null;
  lastFailedAt: string | null;
  recentLogs: BackupLogEntry[];
  schedule: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '—';
  const mb = bytes / 1048576;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

function formatDatetime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 24) return `${Math.floor(h / 24)} ngày trước`;
  if (h > 0) return `${h}h${m}m trước`;
  if (m > 0) return `${m} phút trước`;
  return 'Vừa xong';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ title, icon, children, badge }: {
  title: string;
  icon: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div className="card card-lg" style={{ marginBottom: 20 }}>
      <h3 style={{
        margin: '0 0 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 16,
        fontWeight: 700,
      }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        {title}
        {badge && <span style={{ marginLeft: 'auto' }}>{badge}</span>}
      </h3>
      {children}
    </div>
  );
}

function SettingRow({ label, description, children }: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 0',
      borderBottom: '1px solid var(--border-soft)',
      gap: 24,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-1)', marginBottom: 3 }}>{label}</div>
        {description && (
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.5 }}>{description}</div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

// Toggle Switch component
function Toggle({ checked, onChange, disabled }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        width: 48,
        height: 26,
        borderRadius: 13,
        background: checked ? 'var(--accent)' : 'var(--border)',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s',
        opacity: disabled ? 0.5 : 1,
        padding: 0,
        flexShrink: 0,
      }}
      role="switch"
      aria-checked={checked}
    >
      <span style={{
        position: 'absolute',
        left: checked ? 24 : 2,
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.2s',
      }} />
    </button>
  );
}

// ─── Status Header ────────────────────────────────────────────────────────────

function StatusHeader({ status }: { status: BackupStatus }) {
  const { config, isRunning, lastBackupAt, lastFailedAt } = status;

  let statusBadge: React.ReactNode;
  if (isRunning) {
    statusBadge = <span className="badge" style={{ background: 'var(--info-soft)', color: 'var(--info)', fontSize: 12 }}>🔵 Đang chạy...</span>;
  } else if (!config.isEnabled) {
    statusBadge = <span className="badge" style={{ background: 'var(--warning-soft)', color: 'var(--warning)', fontSize: 12 }}>🟡 Tạm ngưng</span>;
  } else {
    statusBadge = <span className="badge badge-success" style={{ fontSize: 12 }}>🟢 Đang hoạt động</span>;
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 16,
      marginBottom: 20,
    }}>
      {/* Status */}
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trạng thái</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{statusBadge}</div>
      </div>

      {/* Last backup */}
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Backup gần nhất</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>
          {lastBackupAt ? (
            <>
              <span style={{ color: 'var(--success)' }}>✅ </span>
              {formatDatetime(lastBackupAt)}
            </>
          ) : (
            <span style={{ color: 'var(--text-3)' }}>Chưa có backup</span>
          )}
        </div>
        {lastBackupAt && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>{timeAgo(lastBackupAt)}</div>
        )}
      </div>

      {/* Schedule */}
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lịch tự động</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>🕑 02:00 ICT mỗi đêm</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3, fontFamily: 'monospace' }}>cron: 0 19 * * * (UTC)</div>
      </div>

      {/* Google Drive link */}
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lưu trữ</div>
        <a
          href="https://drive.google.com"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-secondary"
          style={{ gap: 6, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
        >
          📁 Mở Google Drive
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
        </a>
        {lastFailedAt && (
          <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>
            ❌ Lần cuối thất bại: {timeAgo(lastFailedAt)}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Config Section ───────────────────────────────────────────────────────────

function ConfigSection({ config, onUpdate }: {
  config: BackupConfig;
  onUpdate: (patch: Partial<BackupConfig>) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);

  async function handleToggle(field: keyof BackupConfig, value: boolean) {
    setSaving(true);
    try {
      await onUpdate({ [field]: value });
      toast(`Đã cập nhật cấu hình`, 'success');
    } catch {
      toast('Lỗi lưu cấu hình', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Cấu hình Backup Tự động" icon="⚙️">
      <SettingRow
        label="Backup tự động"
        description="Bật để hệ thống tự động backup D1 + R2 vào lúc 02:00 ICT mỗi đêm."
      >
        <Toggle
          checked={config.isEnabled}
          onChange={(v) => handleToggle('isEnabled', v)}
          disabled={saving}
        />
      </SettingRow>

      <SettingRow
        label="Tự động xoá sau 30 ngày"
        description="Tự động xoá các bản backup cũ hơn 30 ngày trên Google Drive để tiết kiệm dung lượng."
      >
        <Toggle
          checked={config.autoDelete30d}
          onChange={(v) => handleToggle('autoDelete30d', v)}
          disabled={saving || !config.isEnabled}
        />
      </SettingRow>

      <div style={{ paddingTop: 12, fontSize: 12.5, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>⏰</span>
        Lịch chạy: <code style={{ fontSize: 12, background: 'var(--bg-2)', padding: '1px 6px', borderRadius: 4 }}>0 19 * * *</code> (UTC)
        = <strong>02:00 ICT</strong> mỗi đêm
        {config.updatedAt && (
          <span style={{ marginLeft: 'auto' }}>Cập nhật: {timeAgo(config.updatedAt)}</span>
        )}
      </div>
    </Section>
  );
}

// ─── Manual Trigger ───────────────────────────────────────────────────────────

function ManualTriggerSection({ isRunning, runningInfo, onTrigger }: {
  isRunning: boolean;
  runningInfo: { triggeredBy: string; startedAt: string } | null;
  onTrigger: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const [githubUrl, setGithubUrl] = useState('');

  async function handleConfirm() {
    setConfirming(false);
    setTriggering(true);
    try {
      await onTrigger();
      setTriggered(true);
      setGithubUrl(`https://github.com/haidao/LUYENTHI-2026T04/actions/workflows/backup.yml`);
      setTimeout(() => setTriggered(false), 30000);
    } catch {
      // error đã được toast từ caller
    } finally {
      setTriggering(false);
    }
  }

  if (isRunning && runningInfo) {
    return (
      <Section title="Backup Thủ công" icon="🔁">
        <div style={{
          padding: '20px 24px',
          background: 'color-mix(in srgb, var(--info) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--info) 30%, transparent)',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <div style={{ fontWeight: 700, color: 'var(--info)', fontSize: 15 }}>
            🔵 Backup đang chạy trên GitHub Actions...
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            Bởi: <strong>{runningInfo.triggeredBy}</strong> · Bắt đầu: {formatDatetime(runningInfo.startedAt)}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Quá trình mất khoảng 3–5 phút. Sau khi hoàn tất, log sẽ tự cập nhật.
          </div>
          <a
            href="https://github.com/haidao/LUYENTHI-2026T04/actions/workflows/backup.yml"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-secondary"
            style={{ alignSelf: 'flex-start', marginTop: 4 }}
          >
            📊 Xem tiến trình trên GitHub Actions →
          </a>
        </div>
      </Section>
    );
  }

  return (
    <Section title="Backup Thủ công" icon="🔁">
      {triggered && (
        <div style={{
          padding: '16px 20px',
          background: 'color-mix(in srgb, var(--success) 10%, transparent)',
          border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 16,
        }}>
          <div style={{ fontWeight: 700, color: 'var(--success)', marginBottom: 6 }}>
            ⏳ Backup đã được khởi động!
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8 }}>
            GitHub Actions đang chạy. Quá trình mất khoảng 3–5 phút. Kiểm tra log bên dưới sau khi hoàn tất.
          </div>
          {githubUrl && (
            <a href={githubUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary">
              📊 Xem tiến trình →
            </a>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6 }}>
            Kích hoạt backup ngay lập tức. Hệ thống sẽ export D1 database ra file .sql và sync toàn bộ R2 bucket lên Google Drive.
          </div>
        </div>

        <div>
          {!confirming ? (
            <button
              className="btn btn-primary"
              onClick={() => setConfirming(true)}
              disabled={triggering || triggered}
              style={{ minWidth: 160, gap: 8 }}
            >
              {triggering ? '⏳ Đang khởi động...' : '🔁 Backup Ngay'}
            </button>
          ) : (
            <div style={{
              padding: '14px 18px',
              background: 'var(--warning-soft)',
              border: '1px solid color-mix(in srgb, var(--warning) 40%, transparent)',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              minWidth: 260,
            }}>
              <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-1)' }}>
                ⚠️ Xác nhận backup thủ công?
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                Quá trình sẽ mất ~3–5 phút trên GitHub Actions.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-sm btn-primary" onClick={handleConfirm}>
                  ✅ Xác nhận
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => setConfirming(false)}>
                  Huỷ
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

// ─── Logs Table ───────────────────────────────────────────────────────────────

function LogsSection({ logs }: { logs: BackupLogEntry[] }) {
  if (logs.length === 0) {
    return (
      <Section title="Lịch sử Backup" icon="📋">
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-3)', fontSize: 14 }}>
          Chưa có lịch sử backup. Hệ thống sẽ ghi log sau mỗi lần backup tự động hoặc thủ công.
        </div>
      </Section>
    );
  }

  return (
    <Section
      title="Lịch sử Backup"
      icon="📋"
      badge={<span style={{ fontSize: 12, color: 'var(--text-3)' }}>{logs.length} bản gần nhất</span>}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              {['Thời gian', 'Loại', 'Trạng thái', 'Kích thước SQL', 'Ghi chú'].map(h => (
                <th key={h} style={{
                  padding: '10px 12px',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: 12,
                  color: 'var(--text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr
                key={log.id}
                style={{ borderBottom: '1px solid var(--border-soft)', verticalAlign: 'middle' }}
              >
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--text-1)' }}>
                  <div>{formatDatetime(log.startedAt)}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{timeAgo(log.startedAt)}</div>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <span className="badge" style={{
                    background: log.type === 'manual' ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'var(--bg-2)',
                    color: log.type === 'manual' ? 'var(--accent)' : 'var(--text-2)',
                    fontSize: 12,
                  }}>
                    {log.type === 'auto' ? '🤖 Tự động' : '👤 Thủ công'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {log.status === 'success' && <span style={{ color: 'var(--success)', fontWeight: 600 }}>✅ Thành công</span>}
                  {log.status === 'failed'  && <span style={{ color: 'var(--danger)', fontWeight: 600 }}>❌ Thất bại</span>}
                  {log.status === 'running' && <span style={{ color: 'var(--info)', fontWeight: 600 }}>🔵 Đang chạy</span>}
                </td>
                <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: 'var(--text-2)' }}>
                  {formatBytes(log.sizeSqlBytes)}
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--text-3)', fontSize: 12.5, maxWidth: 280 }}>
                  {log.message || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ─── Restore Guide ────────────────────────────────────────────────────────────

function RestoreGuide() {
  const [open, setOpen] = useState(false);

  return (
    <Section title="Hướng dẫn Khôi phục (Restore)" icon="🔄">
      <button
        className="btn btn-sm btn-secondary"
        onClick={() => setOpen(!open)}
        style={{ marginBottom: open ? 16 : 0 }}
      >
        {open ? '▲ Thu gọn' : '▼ Xem hướng dẫn khôi phục database'}
      </button>

      {open && (
        <div style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.7 }}>
          <p style={{ margin: '0 0 12px', fontWeight: 600, color: 'var(--text-1)' }}>
            Quy trình khôi phục D1 Database từ file backup:
          </p>

          <ol style={{ margin: 0, padding: '0 0 0 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <li>
              <strong>Tải file backup từ Google Drive:</strong>
              <br/>
              Mở folder <em>luyenthi-backup/YYYY-MM-DD/</em> → tải file <code>.sql</code>
            </li>
            <li>
              <strong>Khôi phục vào D1 (tài khoản hiện tại hoặc mới):</strong>
              <pre style={{
                background: 'var(--bg-2)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
                margin: '6px 0 0',
                fontSize: 12,
                overflowX: 'auto',
                fontFamily: 'monospace',
              }}>
{`# Tạo database mới nếu cần
npx wrangler d1 create luyen-thi-db

# Khôi phục từ file SQL backup
npx wrangler d1 execute luyen-thi-db \\
  --file luyen-thi-db-backup-YYYY-MM-DD.sql \\
  --remote`}
              </pre>
            </li>
            <li>
              <strong>Cập nhật database_id mới vào wrangler.json</strong> (nếu tạo DB mới)
            </li>
            <li>
              <strong>Khôi phục R2:</strong>
              <pre style={{
                background: 'var(--bg-2)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
                margin: '6px 0 0',
                fontSize: 12,
                overflowX: 'auto',
                fontFamily: 'monospace',
              }}>
{`# Sync từ Google Drive về R2 mới
rclone sync "gdrive:luyenthi-backup/r2-luyen-thi-content" \\
  ":s3,provider=Cloudflare,...:luyen-thi-content"`}
              </pre>
            </li>
          </ol>

          <div style={{
            marginTop: 14,
            padding: '10px 14px',
            background: 'var(--warning-soft)',
            borderRadius: 'var(--radius-md)',
            fontSize: 12.5,
            color: 'var(--text-2)',
          }}>
            ⚠️ Sau khi khôi phục, cần deploy lại Worker với config mới và kiểm tra tất cả tính năng.
          </div>
        </div>
      )}
    </Section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function BackupPage() {
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await adminFetch<BackupStatus>('/backup/status');
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải trạng thái backup');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();

    // Auto-refresh mỗi 30 giây nếu đang chạy
    const interval = setInterval(() => {
      if (status?.isRunning) {
        fetchStatus();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchStatus, status?.isRunning]);

  async function handleUpdateConfig(patch: Partial<BackupStatus['config']>) {
    const res = await adminFetch<{ ok: boolean; config: BackupStatus['config'] }>('/backup/config', {
      method: 'POST',
      body: JSON.stringify(patch),
    });
    if (status) {
      setStatus({ ...status, config: res.config });
    }
  }

  async function handleTrigger() {
    try {
      await adminFetch('/backup/trigger', { method: 'POST' });
      toast('⏳ Backup đã được khởi động trên GitHub Actions!', 'success');
      // Refresh sau 3 giây để cập nhật isRunning
      setTimeout(() => fetchStatus(), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Không thể kích hoạt backup';
      toast(`❌ ${msg}`, 'error');
      throw err;
    }
  }

  if (isLoading) {
    return (
      <div>
        <div className="page-header">
          <div className="page-header__left">
            <h1>🗄️ Backup</h1>
            <p>Quản lý sao lưu D1 Database + R2 Storage → Google Drive</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-3)', fontSize: 14 }}>
          ⏳ Đang tải trạng thái backup...
        </div>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div>
        <div className="page-header">
          <div className="page-header__left">
            <h1>🗄️ Backup</h1>
            <p>Quản lý sao lưu D1 Database + R2 Storage → Google Drive</p>
          </div>
        </div>
        <div className="card card-lg" style={{ textAlign: 'center', padding: '40px', color: 'var(--danger)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>❌ Không thể tải dữ liệu</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16 }}>{error}</div>
          <button className="btn btn-secondary btn-sm" onClick={fetchStatus}>🔄 Thử lại</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>🗄️ Backup</h1>
          <p>Sao lưu tự động D1 Database + R2 Storage → Google Drive · {status.schedule}</p>
        </div>
        <div className="page-header__actions">
          <button
            className="btn btn-ghost btn-sm"
            onClick={fetchStatus}
            title="Làm mới trạng thái"
          >
            🔄 Làm mới
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <StatusHeader status={status} />

      {/* Config */}
      <ConfigSection config={status.config} onUpdate={handleUpdateConfig} />

      {/* Manual Trigger */}
      <ManualTriggerSection
        isRunning={status.isRunning}
        runningInfo={status.runningInfo}
        onTrigger={handleTrigger}
      />

      {/* Logs */}
      <LogsSection logs={status.recentLogs} />

      {/* Restore Guide */}
      <RestoreGuide />
    </div>
  );
}
